import useAutocomplete from "@mui/material/useAutocomplete";
import { useMemo, type ReactNode } from "react";
import { FieldMarker } from "@/components/FieldMarker";
import type { SelectOption } from "@/components/SelectField";
import { TOUCH_SQUARE } from "@/components/buttonStyles";
import {
  FIELD_CONTROL,
  FIELD_ERROR,
  FIELD_HELP,
  FIELD_LABEL,
  FIELD_LABEL_ERROR,
  FIELD_OPTION,
} from "@/components/fieldStyles";

// BRK-S314 — the searchable select, for a closed list too long to scan (US
// states first): type to filter, anywhere in the label and in any case, or open
// it to browse. Controlled via `value`/`onChange` with "" for nothing chosen,
// and it emits the option's VALUE ("CA"), never its label (D-10).
//
// The keyboard and ARIA model is MUI's headless `useAutocomplete`, so it's the
// one a screen reader expects of a combobox: arrows move, Enter picks, Escape
// closes, the active option is the input's aria-activedescendant. The markup is
// ours, on the field styles, so it reads as the SelectField it replaces.
// `Typeahead` is the server-backed sibling on the same hook and the same parts
// below, so a broker can't tell a static list from a search by how it behaves.

interface SearchSelectProps {
  label: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  /** Shown when nothing is chosen. */
  placeholder?: string;
  /** A line under the label: how to read the options, say. */
  help?: string;
  error?: string;
  /** A required field has no clear button, and erasing its text doesn't
   *  empty it: its answer changes only on a pick, with no empty one in
   *  between for what reads it to act on. */
  required?: boolean;
  /** Show the muted "(optional)" marker. */
  optional?: boolean;
  disabled?: boolean;
  id?: string;
}

export function SearchSelect({
  label,
  options,
  value,
  onChange,
  placeholder,
  help,
  error,
  required,
  optional,
  disabled,
  id: idProp,
}: SearchSelectProps) {
  // The chosen option by value and label, not by the caller's object: a
  // parent that rebuilds its options each render must not reset what the
  // broker is typing (the hook resets the box when its value changes).
  const chosen = options.find((o) => o.value === value)?.label;
  const selected = useMemo(
    () => (chosen === undefined ? null : { value, label: chosen }),
    [value, chosen],
  );
  const combo = useAutocomplete({
    id: idProp,
    options,
    value: selected,
    onChange: (_event, option) => onChange(option?.value ?? ""),
    getOptionLabel: (o) => o.label,
    getOptionKey: (o) => o.value,
    isOptionEqualToValue: (o, v) => o.value === v.value,
    openOnFocus: true,
    autoHighlight: true,
    disableClearable: Boolean(required),
    disabled,
  });
  const showing = combo.groupedOptions;
  return (
    <ComboboxField
      combo={combo}
      label={label}
      help={help}
      error={error}
      required={required}
      optional={optional}
      placeholder={placeholder}
      clearable={!required && !disabled && selected !== null}
      note={showing.length === 0 ? "No matches" : null}
      options={showing.map((option, index) => {
        const { key, ...props } = combo.getOptionProps({ option, index });
        return (
          <li key={key} {...props} className={FIELD_OPTION}>
            {option.label}
          </li>
        );
      })}
    />
  );
}

type Combobox = Pick<
  ReturnType<typeof useAutocomplete>,
  | "id"
  | "popupOpen"
  | "getRootProps"
  | "getInputLabelProps"
  | "getInputProps"
  | "getClearProps"
  | "getListboxProps"
>;

/** The parts SearchSelect and Typeahead share: the label, the box, the clear
 *  button and the popup. Each brings its own hook and its own options. */
export function ComboboxField({
  combo,
  label,
  help,
  error,
  required,
  optional,
  placeholder,
  clearable,
  note,
  options,
  detail,
}: {
  combo: Combobox;
  label: string;
  help?: string;
  /** A line under the box about the pick, which describes it, such as a
   *  class code's wage tier (BRK-S394). */
  detail?: string;
  error?: string;
  required?: boolean;
  optional?: boolean;
  placeholder?: string;
  clearable: boolean;
  /** Said in the popup when it lists nothing, or alongside a search. */
  note: ReactNode;
  options: ReactNode[];
}) {
  const errorId = `${combo.id}-error`;
  const helpId = `${combo.id}-help`;
  const detailId = `${combo.id}-detail`;
  const describedBy =
    [help && helpId, detail && detailId, error && errorId]
      .filter(Boolean)
      .join(" ") || undefined;
  const listed = options.length > 0;
  return (
    <div className="flex w-full min-w-0 flex-col gap-1 text-left">
      <span className="flex items-baseline gap-1.5">
        <label
          {...combo.getInputLabelProps()}
          className={error ? FIELD_LABEL_ERROR : FIELD_LABEL}
        >
          {label}
        </label>
        <FieldMarker required={required} optional={optional} />
      </span>
      {help && (
        <p id={helpId} className={FIELD_HELP}>
          {help}
        </p>
      )}
      <div {...combo.getRootProps()} className="relative">
        <input
          {...combo.getInputProps()}
          placeholder={placeholder}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={`${FIELD_CONTROL} truncate pr-9 [@media(pointer:coarse)]:pr-12`}
        />
        {clearable ? (
          <button
            {...combo.getClearProps()}
            aria-label={`Clear ${label}`}
            className={`absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded text-brk-dim transition-colors hover:text-brk-text ${TOUCH_SQUARE}`}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        ) : (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-brk-dim"
          >
            ▾
          </span>
        )}
        {combo.popupOpen && (listed || note) && (
          <div className="absolute z-20 mt-1 w-full overflow-hidden rounded-md border border-brk-border bg-brk-panel shadow-lg">
            {note && (
              <div className="px-3 py-2 text-sm text-brk-dim">{note}</div>
            )}
            {listed && (
              <ul
                {...combo.getListboxProps()}
                className="max-h-60 overflow-auto py-1"
              >
                {options}
              </ul>
            )}
          </div>
        )}
      </div>
      {detail && (
        <p id={detailId} className={FIELD_HELP}>
          {detail}
        </p>
      )}
      {error && (
        <p id={errorId} className={FIELD_ERROR}>
          {error}
        </p>
      )}
    </div>
  );
}
